import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { AlertTriangle, CheckCircle2, Clock, Gauge } from "lucide-react";

const STATUS_META = {
  ok: { label: "OK", cls: "bg-emerald-50 text-emerald-700", Icon: CheckCircle2, bar: "#16a34a" },
  tight: { label: "Tight", cls: "bg-amber-50 text-amber-700", Icon: Clock, bar: "#f59e0b" },
  overloaded: { label: "Overloaded", cls: "bg-red-50 text-red-700", Icon: AlertTriangle, bar: "#dc2626" },
};

function fmtHours(min) {
  if (!min) return "0.0 h";
  return `${(min / 60).toFixed(1)} h`;
}

export default function CapacityPage() {
  const [horizon, setHorizon] = useState(7);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async (h) => {
    const { data } = await api.get(`/capacity/load?horizon_days=${h}`);
    setData(data);
    setSelected((cur) => {
      if (!cur) return data.workcenters[0] || null;
      return data.workcenters.find((w) => w.workcenter_id === cur.workcenter_id) || data.workcenters[0] || null;
    });
  };

  useEffect(() => { load(horizon); /* eslint-disable-next-line */ }, [horizon]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.workcenters.map((w) => ({
      name: w.code,
      Planned: w.by_status.planned,
      Released: w.by_status.released,
      "In Progress": w.by_status.in_progress,
      Capacity: w.capacity_min,
    }));
  }, [data]);

  if (!data) return <div className="text-slate-500 text-sm" data-testid="cap-loading">Loading capacity...</div>;

  const totalUtil = data.totals.util_pct;
  const totalUtilColor = totalUtil > 100 ? "text-red-700" : totalUtil >= 85 ? "text-amber-700" : "text-emerald-700";

  return (
    <div className="space-y-6" data-testid="capacity-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Planning</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Capacity & Load
          </h1>
          <p className="text-sm text-slate-500 mt-1">Open work orders × routing cycle times vs. each workcenter's available minutes.</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs uppercase tracking-[0.14em] text-slate-500">Horizon</Label>
            <Select value={String(horizon)} onValueChange={(v) => setHorizon(parseInt(v, 10))}>
              <SelectTrigger className="w-44 mt-1.5" data-testid="capacity-horizon-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 day</SelectItem>
                <SelectItem value="3">3 days</SelectItem>
                <SelectItem value="7">1 week</SelectItem>
                <SelectItem value="14">2 weeks</SelectItem>
                <SelectItem value="30">1 month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="kpi-card" data-testid="kpi-total-cap">
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Total Capacity</div>
          <div className="mt-2 text-3xl font-extrabold text-slate-900 mono" style={{ fontFamily: "Manrope" }}>
            {fmtHours(data.totals.total_capacity_min)}
          </div>
          <div className="text-xs text-slate-500 mt-1">{horizon} day window</div>
        </div>
        <div className="kpi-card" data-testid="kpi-total-load">
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Total Load</div>
          <div className="mt-2 text-3xl font-extrabold text-slate-900 mono" style={{ fontFamily: "Manrope" }}>
            {fmtHours(data.totals.total_load_min)}
          </div>
          <div className="text-xs text-slate-500 mt-1">{data.totals.open_wo_count} open WOs</div>
        </div>
        <div className="kpi-card" data-testid="kpi-util">
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Utilization</div>
          <div className={`mt-2 text-3xl font-extrabold ${totalUtilColor} mono`} style={{ fontFamily: "Manrope" }}>
            {totalUtil}%
          </div>
          <div className="text-xs text-slate-500 mt-1">All workcenters</div>
        </div>
        <div className="kpi-card" data-testid="kpi-overloaded">
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">Overloaded WCs</div>
          <div className="mt-2 text-3xl font-extrabold text-red-700" style={{ fontFamily: "Manrope" }}>
            {data.workcenters.filter((w) => w.status === "overloaded").length}
          </div>
          <div className="text-xs text-slate-500 mt-1">Util &gt; 100%</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Load vs. Capacity (minutes)</h3>
          <span className="text-xs text-slate-500">Stacked by WO status, dashed line = capacity</span>
        </div>
        <div className="h-80">
          <ResponsiveContainer>
            <BarChart data={chartData}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 12 }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 12 }} unit=" min" width={80} />
              <Tooltip cursor={{ fill: "rgba(37,99,235,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Planned" stackId="load" fill="#94a3b8" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Released" stackId="load" fill="#f59e0b" radius={[0, 0, 0, 0]} />
              <Bar dataKey="In Progress" stackId="load" fill="#2563eb" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Capacity" fill="transparent" stroke="#dc2626" strokeDasharray="6 4" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Workcenters by Utilisation</div>
          <div className="divide-y divide-slate-100">
            {data.workcenters.map((w) => {
              const meta = STATUS_META[w.status];
              const widthPct = Math.min(100, w.util_pct);
              const overflowPct = Math.max(0, w.util_pct - 100);
              return (
                <button
                  key={w.workcenter_id}
                  onClick={() => setSelected(w)}
                  data-testid={`cap-wc-${w.code}`}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.workcenter_id === w.workcenter_id ? "bg-blue-50/60" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs mono text-slate-500 uppercase tracking-[0.14em]">{w.code}</div>
                      <div className="text-sm font-semibold text-slate-800">{w.name}</div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${meta.cls}`}>
                      <meta.Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </div>
                  {/* utilisation bar */}
                  <div className="mt-2.5 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="h-full" style={{ width: `${widthPct}%`, backgroundColor: meta.bar }} />
                    {overflowPct > 0 && (
                      <div className="h-full bg-red-700" style={{ width: `${Math.min(100, overflowPct)}%`, opacity: 0.55 }} />
                    )}
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500 mono">
                    <span>{fmtHours(w.load_min)} / {fmtHours(w.capacity_min)}</span>
                    <span className={w.util_pct > 100 ? "text-red-700 font-semibold" : ""}>{w.util_pct}%</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[40vh]" data-testid="cap-detail">
          {!selected ? (
            <div className="text-sm text-slate-500">Select a workcenter.</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>{selected.name}</h2>
                  <div className="text-xs text-slate-500 mt-1">
                    {selected.shift_hours_per_day}h/day · ₹{selected.cost_per_hour}/h · {horizon}-day window
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Utilisation</div>
                  <div className={`text-3xl font-extrabold mono ${selected.util_pct > 100 ? "text-red-700" : selected.util_pct >= 85 ? "text-amber-700" : "text-emerald-700"}`} style={{ fontFamily: "Manrope" }}>
                    {selected.util_pct}%
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Capacity</div>
                  <div className="text-lg font-semibold mono">{fmtHours(selected.capacity_min)}</div>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">Load</div>
                  <div className="text-lg font-semibold mono">{fmtHours(selected.load_min)}</div>
                </div>
                <div className="border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{selected.overload_min > 0 ? "Overload" : "Available"}</div>
                  <div className={`text-lg font-semibold mono ${selected.overload_min > 0 ? "text-red-700" : "text-emerald-700"}`}>
                    {selected.overload_min > 0 ? fmtHours(selected.overload_min) : fmtHours(selected.available_min)}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Contributing Work Orders</div>
                {selected.by_wo.length === 0 ? (
                  <div className="text-sm text-slate-500 py-6 text-center">No open WOs hitting this workcenter.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                        <th className="py-2 px-3">WO</th><th className="py-2 px-3">Item</th>
                        <th className="py-2 px-3">Qty</th><th className="py-2 px-3">Op</th>
                        <th className="py-2 px-3">Status</th><th className="py-2 px-3">Minutes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.by_wo.map((r, idx) => (
                        <tr key={idx} className="border-t border-slate-100" data-testid={`cap-row-${selected.code}-${r.wo_code}`}>
                          <td className="py-2 px-3 mono font-medium text-blue-700">{r.wo_code}</td>
                          <td className="py-2 px-3 text-slate-700">{r.item_code} <span className="text-slate-500 text-xs">— {r.item_name}</span></td>
                          <td className="py-2 px-3 mono">{r.qty}</td>
                          <td className="py-2 px-3 text-xs text-slate-600">{r.seq} · {r.description}</td>
                          <td className="py-2 px-3">
                            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full
                              ${r.status === "in_progress" ? "bg-blue-50 text-blue-700" : r.status === "released" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                              {r.status.replace("_", " ")}
                            </span>
                          </td>
                          <td className="py-2 px-3 mono font-semibold">{r.minutes.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {selected.status === "overloaded" && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800 flex items-start gap-2" data-testid="overload-warning">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>{selected.code}</strong> is over capacity by <span className="mono">{fmtHours(selected.overload_min)}</span>. Consider extending the shift, shifting WOs to another workcenter, or splitting an operation.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {data.totals.skipped_wos.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800" data-testid="skipped-wos">
          <div className="flex items-center gap-2 mb-1 font-semibold">
            <Gauge className="w-4 h-4" /> {data.totals.skipped_wos.length} open WO(s) skipped (no routing)
          </div>
          <div className="text-xs">
            {data.totals.skipped_wos.map((s) => `${s.code} (${s.item_code} × ${s.qty})`).join(" · ")}
          </div>
        </div>
      )}
    </div>
  );
}
